import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { uiConfig } from '../config';
import { useRoleAccess } from '../hooks/useRoleAccess';
import AnalysisStatusCaption, { type AnalysisStatus } from '../components/training/AnalysisStatusCaption';
import moment from 'moment';
import { DATE_TIME_FORMAT } from '../constants/dateFormats';
import TrackTypeBadge from '../components/training/TrackTypeBadge';

interface TrainingSession {
  id: string; // Mongoose _id transformed to id by toJSON
  _id?: string; // Fallback for backward compatibility
  sessionId: string;
  trackId: string;
  trackName: string;
  trackType: 'kb' | 'playbook';
  userId: string;
  userName: string;
  userEmail: string;
  // Matches backend/src/models/TrainingSession.js's real enum — the
  // frontend previously spelled the live state 'in_progress', which never
  // matches the actual 'active' value written by the backend (so both the
  // status filter and the status-color chip silently never matched a live
  // session).
  status: 'active' | 'completed' | 'failed' | 'cancelled' | 'abandoned';
  // Training quality score (DB field behavioralScore) from post-training
  // analysis. null until analysis runs.
  behavioralScore: number | null;
  // Completion % is an analysis output too — null until analysis finishes,
  // not to be confused with a genuine 0%.
  completionPercentage: number | null;
  // Where the analysis stands; null before it has started.
  analysisStatus?: AnalysisStatus | null;
  itemCount: number;
  completedItems: number;
  startedAt: string;
  completedAt?: string;
  duration?: number;
}

interface TrainingSessionsProps {
  /**
   * Header slot to render the toolbar into. Omit it for an inline row;
   * pass null while the slot is still mounting so nothing flashes inline.
   */
  actionsContainer?: HTMLElement | null;
  /** Tab-row slot for the filters; same omit / null contract. */
  filtersContainer?: HTMLElement | null;
}

const TrainingSessions: React.FC<TrainingSessionsProps> = ({ actionsContainer, filtersContainer }) => {
  const navigate = useNavigate();
  const { isAdmin } = useRoleAccess();
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(uiConfig.defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('limit', String(rowsPerPage));

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      // Use admin endpoint if admin, otherwise user endpoint
      const endpoint = isAdmin
        ? `/training/admin/sessions?${params.toString()}`
        : `/training/sessions?${params.toString()}`;

      const response = await apiService.get(endpoint);

      if (response.success) {
        setSessions(response.data.sessions || []);
        setTotalCount(response.data.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error('Error fetching training sessions:', error);
      setError(error.message || 'Failed to fetch training sessions');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, isAdmin]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleViewSession = (session: TrainingSession) => {
    navigate(`/training/sessions/${session.id || session._id}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'active':
        return 'info';
      case 'abandoned':
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const roundedSeconds = Math.floor(seconds);
    const minutes = Math.floor(roundedSeconds / 60);
    const secs = roundedSeconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format(DATE_TIME_FORMAT);
  };

  const toolbar = (
    <IconButton onClick={fetchSessions} color="primary" aria-label="Refresh sessions">
      <RefreshIcon />
    </IconButton>
  );

  // Filters change the query, so they always start from the first page.
  const filters = (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel id="training-session-status-label">Status</InputLabel>
      <Select
        labelId="training-session-status-label"
        value={statusFilter}
        onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
        label="Status"
      >
        <MenuItem value="all">All</MenuItem>
        <MenuItem value="completed">Completed</MenuItem>
        <MenuItem value="active">In Progress</MenuItem>
        <MenuItem value="abandoned">Abandoned</MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <Box>
      {filtersContainer === undefined ? (
        <Box display="flex" justifyContent="flex-end" mb={2}>{filters}</Box>
      ) : filtersContainer ? (
        createPortal(filters, filtersContainer)
      ) : null}
      {actionsContainer === undefined ? (
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
          {toolbar}
        </Box>
      ) : actionsContainer ? (
        createPortal(toolbar, actionsContainer)
      ) : null}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {isAdmin && <TableCell>User</TableCell>}
                <TableCell>Track</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Training Quality</TableCell>
                <TableCell>Completion</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Started At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 6} align="center">
                    No training sessions found.
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((session) => (
                  <TableRow
                    key={session.id || session._id}
                    hover
                    onClick={() => handleViewSession(session)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {isAdmin && (
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{session.userName}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {session.userEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {session.trackName}
                      </Typography>
                      <Box mt={0.5}>
                        <TrackTypeBadge trackType={session.trackType} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={session.status === 'active' ? 'In Progress' : session.status}
                        color={getStatusColor(session.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {session.status === 'active' ? (
                        <Typography variant="caption" color="textSecondary">Session in progress</Typography>
                      ) : session.behavioralScore != null ? (
                        <Chip
                          label={`${session.behavioralScore}%`}
                          color={getScoreColor(session.behavioralScore)}
                          size="small"
                        />
                      ) : (
                        <AnalysisStatusCaption analysisStatus={session.analysisStatus} />
                      )}
                    </TableCell>
                    <TableCell>
                      {session.status === 'active' ? (
                        <Typography variant="caption" color="textSecondary">—</Typography>
                      ) : session.completionPercentage != null ? (
                        `${session.completionPercentage}%`
                      ) : (
                        <AnalysisStatusCaption analysisStatus={session.analysisStatus} />
                      )}
                    </TableCell>
                    <TableCell>{formatDuration(session.duration)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(session.startedAt)}</Typography>
                      {session.completedAt && (
                        <Typography variant="caption" color="textSecondary">
                          Ended: {formatDate(session.completedAt)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
};

export default TrainingSessions;
